import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getHeatLabel } from '@/hooks/useHeatCounter';
import { 
  Clock, 
  Route, 
  Gauge, 
  ChevronRight, 
  Plus,
  Calendar,
  Trophy,
  Waves,
  LogOut,
  User,
  Zap
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// DBPRO Brand Colors
const BRAND = {
  navyDark: '#0A1A2F',
  navyMedium: '#0F2847',
  navyCard: '#1A2E4A',
  navyLight: '#243B5A',
  red: '#E53935',
  redHover: '#C62828',
  orange: '#FF6B35',
  white: '#FFFFFF',
  lightGray: '#F5F5F5',
  mediumGray: '#9CA3AF',
  darkGray: '#6B7280'
};

// Logo URL
const DBPRO_LOGO_URL = 'https://customer-assets.emergentagent.com/job_gps-tracker-dash-1/artifacts/tln5whso_Final%20brand%20logo.png';

// Format duration from seconds to mm:ss
const formatDuration = (seconds) => {
  const mins = Math.floor(seconds / 60);
  const secs = Math.round(seconds % 60);
  return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
};

// Format distance
const formatDistance = (meters) => {
  if (meters >= 1000) {
    return `${(meters / 1000).toFixed(2)} km`;
  }
  return `${Math.round(meters)} m`;
};

// Format speed
const formatSpeed = (ms) => {
  return (ms * 3.6).toFixed(1); // Convert to km/h
};

// Format date
const formatDate = (dateString) => {
  const date = new Date(dateString);
  const now = new Date();
  const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
  
  if (diffDays === 0) {
    return `Today, ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
  } else if (diffDays === 1) {
    return `Yesterday, ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
  } else if (diffDays < 7) {
    return `${diffDays} days ago`;
  } else {
    return date.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' });
  }
};

const HistoryScreen_DBPRO = () => {
  const navigate = useNavigate();
  const [sessions, setSessions] = useState([]);
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [syncMessage, setSyncMessage] = useState(null);

  // Fix 2: Retry failed sessions from localStorage
  const retryFailedSessions = async (token) => {
    const failedSessions = JSON.parse(localStorage.getItem('dbpro_failed_sessions') || '[]');
    if (failedSessions.length === 0) return;
    
    setSyncMessage(`Syncing ${failedSessions.length} pending session(s)...`);
    const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
    const successfullyRetried = [];
    
    for (let i = 0; i < failedSessions.length; i++) {
      const session = failedSessions[i];
      try {
        // Remove local metadata before sending
        const { failed_at, created_at_local, ...sessionData } = session;
        const response = await fetch(`${API}/sessions`, {
          method: 'POST',
          headers: { ...headers, 'Content-Type': 'application/json' },
          body: JSON.stringify(sessionData)
        });
        
        // Only mark as successful if server returned 2xx
        if (response.ok) {
          successfullyRetried.push(i);
        } else {
          console.error('Failed to retry session, server returned:', response.status);
        }
      } catch (err) {
        console.error('Failed to retry session:', err);
      }
    }
    
    // Remove successfully retried sessions from localStorage
    if (successfullyRetried.length > 0) {
      const remainingFailed = failedSessions.filter((_, idx) => !successfullyRetried.includes(idx));
      localStorage.setItem('dbpro_failed_sessions', JSON.stringify(remainingFailed));
      
      setSyncMessage(`✓ Synced ${successfullyRetried.length} session(s) to your history`);
      
      // Clear message after 3 seconds
      setTimeout(() => setSyncMessage(null), 3000);
      
      // Return true to indicate history should be refreshed
      return true;
    }
    
    if (failedSessions.length > 0 && successfullyRetried.length === 0) {
      setSyncMessage(`⚠ Unable to sync ${failedSessions.length} session(s). Check your connection.`);
      setTimeout(() => setSyncMessage(null), 5000);
    }
    
    return false;
  };

  // Fetch user and sessions
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Get token from localStorage as backup
        let storedToken = localStorage.getItem('dbpro_session_token');
        let headers = storedToken ? { 'Authorization': `Bearer ${storedToken}` } : {};
        
        // Fix 3: Refresh token before fetching data
        try {
          if (storedToken) {
            const refreshResponse = await fetch(`${API}/auth/refresh`, {
              method: 'POST',
              headers
            });
            if (refreshResponse.ok) {
              const refreshData = await refreshResponse.json();
              if (refreshData.session_token) {
                storedToken = refreshData.session_token;
                localStorage.setItem('dbpro_session_token', storedToken);
                headers = { 'Authorization': `Bearer ${storedToken}` };
              }
            }
          }
        } catch (refreshErr) {
          console.warn('Token refresh failed:', refreshErr);
        }
        
        // Fetch user info
        const userResponse = await fetch(`${API}/auth/me`, {
          headers
        });
        
        if (!userResponse.ok) {
          navigate('/', { replace: true });
          return;
        }
        
        const userData = await userResponse.json();
        setUser(userData);
        
        // Fix 2: Retry any failed sessions before fetching history
        const needsRefresh = await retryFailedSessions(storedToken);
        
        // Fetch history
        const historyResponse = await fetch(`${API}/history`, {
          headers
        });
        
        if (historyResponse.ok) {
          const historyData = await historyResponse.json();
          setSessions(historyData);
        }
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Failed to load history');
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchData();
  }, [navigate]);

  const handleLogout = async () => {
    try {
      const storedToken = localStorage.getItem('dbpro_session_token');
      const headers = storedToken ? { 'Authorization': `Bearer ${storedToken}` } : {};
      await fetch(`${API}/auth/logout`, { method: 'POST', headers });
    } catch (err) {
      console.error('Logout error:', err);
    }
    // Clear localStorage
    localStorage.removeItem('dbpro_session_token');
    localStorage.removeItem('dbpro_user');
    navigate('/', { replace: true });
  };

  const handleStartNewSession = () => {
    navigate('/dashboard');
  };

  const handleViewSession = (session) => {
    // Navigate to summary with session data
    navigate('/summary', { 
      state: {
        totalDistance: session.distance_covered,
        duration: session.duration_seconds,
        averageSpeed: session.average_speed_ms,
        maxSpeed: session.max_speed_ms,
        targetDistance: session.target_distance,
        mode: session.mode,
        coordinates: session.coordinates || [],
        autoStopped: session.auto_stopped,
        heatNumber: session.heat_number || 0
      }
    });
  };

  // Calculate stats
  const totalSessions = sessions.length;
  const totalDistance = sessions.reduce((acc, s) => acc + (s.distance_covered || 0), 0);
  const totalTime = sessions.reduce((acc, s) => acc + (s.duration_seconds || 0), 0);
  const avgSpeed = sessions.length > 0 
    ? sessions.reduce((acc, s) => acc + (s.average_speed_ms || 0), 0) / sessions.length 
    : 0;

  if (isLoading) {
    return (
      <div 
        className="min-h-screen flex items-center justify-center"
        style={{ backgroundColor: BRAND.navyDark }}
      >
        <div className="animate-pulse text-white text-lg">Loading history...</div>
      </div>
    );
  }

  return (
    <div 
      className="min-h-screen flex flex-col"
      style={{ backgroundColor: BRAND.navyDark }}
      data-testid="history-screen"
    >
      {/* Header */}
      <header 
        className="px-4 py-4 flex items-center justify-between"
        style={{ backgroundColor: BRAND.navyMedium }}
        data-testid="history-header"
      >
        <img
          src={DBPRO_LOGO_URL}
          alt="DBPRO Logo"
          className="h-10 w-auto object-contain"
          data-testid="history-logo"
        />
        
        {/* User Menu */}
        <div className="relative">
          <button
            onClick={() => setShowUserMenu(!showUserMenu)}
            className="flex items-center gap-2 px-3 py-2 rounded-lg transition-colors"
            style={{ backgroundColor: BRAND.navyCard }}
            data-testid="user-menu-button"
          >
            {user?.picture ? (
              <img 
                src={user.picture} 
                alt={user.name} 
                className="w-8 h-8 rounded-full"
              />
            ) : (
              <User className="w-6 h-6" style={{ color: BRAND.white }} />
            )}
            <span className="text-sm font-medium hidden sm:block" style={{ color: BRAND.white }}>
              {user?.name || 'User'}
            </span>
          </button>
          
          {showUserMenu && (
            <div 
              className="absolute right-0 mt-2 w-48 rounded-lg shadow-lg overflow-hidden z-50"
              style={{ backgroundColor: BRAND.navyCard }}
              data-testid="user-dropdown"
            >
              <div className="px-4 py-3 border-b" style={{ borderColor: BRAND.navyLight }}>
                <p className="text-sm font-medium" style={{ color: BRAND.white }}>{user?.name}</p>
                <p className="text-xs" style={{ color: BRAND.mediumGray }}>{user?.email}</p>
              </div>
              <button
                onClick={handleLogout}
                className="w-full px-4 py-3 text-left flex items-center gap-2 hover:bg-opacity-80 transition-colors"
                style={{ color: BRAND.red }}
                data-testid="logout-button"
              >
                <LogOut className="w-4 h-4" />
                <span className="text-sm">Sign Out</span>
              </button>
            </div>
          )}
        </div>
      </header>

      {/* Sync Message Banner */}
      {syncMessage && (
        <div 
          className="px-4 py-3 text-center text-sm font-medium"
          style={{ 
            backgroundColor: syncMessage.includes('✓') ? '#065F46' : syncMessage.includes('⚠') ? '#92400E' : BRAND.navyCard,
            color: BRAND.white 
          }}
          data-testid="sync-message"
        >
          {syncMessage}
        </div>
      )}

      {/* Stats Summary */}
      <div 
        className="px-4 py-4"
        style={{ backgroundColor: BRAND.navyMedium }}
      >
        <h1 
          className="text-xl font-bold mb-4"
          style={{ color: BRAND.white }}
          data-testid="history-title"
        >
          Training History
        </h1>
        
        <div className="grid grid-cols-2 gap-3" data-testid="stats-summary">
          {/* Total Sessions */}
          <div 
            className="rounded-lg p-3"
            style={{ backgroundColor: BRAND.navyCard }}
            data-testid="stat-sessions"
          >
            <div className="flex items-center gap-2 mb-1">
              <Calendar className="w-4 h-4" style={{ color: BRAND.orange }} />
              <span className="text-xs" style={{ color: BRAND.mediumGray }}>Sessions</span>
            </div>
            <p className="text-xl font-bold" style={{ color: BRAND.white }}>{totalSessions}</p>
          </div>
          
          {/* Total Distance */}
          <div 
            className="rounded-lg p-3"
            style={{ backgroundColor: BRAND.navyCard }}
            data-testid="stat-distance"
          >
            <div className="flex items-center gap-2 mb-1">
              <Route className="w-4 h-4" style={{ color: BRAND.orange }} />
              <span className="text-xs" style={{ color: BRAND.mediumGray }}>Total Distance</span>
            </div>
            <p className="text-xl font-bold" style={{ color: BRAND.white }}>{formatDistance(totalDistance)}</p>
          </div>
          
          {/* Total Time */}
          <div 
            className="rounded-lg p-3"
            style={{ backgroundColor: BRAND.navyCard }}
            data-testid="stat-time"
          >
            <div className="flex items-center gap-2 mb-1">
              <Clock className="w-4 h-4" style={{ color: BRAND.orange }} />
              <span className="text-xs" style={{ color: BRAND.mediumGray }}>Total Time</span>
            </div>
            <p className="text-xl font-bold" style={{ color: BRAND.white }}>{formatDuration(totalTime)}</p>
          </div>
          
          {/* Avg Speed */}
          <div 
            className="rounded-lg p-3"
            style={{ backgroundColor: BRAND.navyCard }}
            data-testid="stat-speed"
          >
            <div className="flex items-center gap-2 mb-1">
              <Gauge className="w-4 h-4" style={{ color: BRAND.orange }} />
              <span className="text-xs" style={{ color: BRAND.mediumGray }}>Avg Speed</span>
            </div>
            <p className="text-xl font-bold" style={{ color: BRAND.white }}>{formatSpeed(avgSpeed)} km/h</p>
          </div>
        </div>
      </div>

      {/* Sessions List */}
      <main className="flex-1 px-4 py-4 overflow-y-auto">
        {error ? (
          <div 
            className="text-center py-8"
            style={{ color: BRAND.red }}
            data-testid="error-message"
          >
            {error}
          </div>
        ) : sessions.length === 0 ? (
          <div 
            className="text-center py-12"
            data-testid="empty-state"
          >
            <div 
              className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4"
              style={{ backgroundColor: BRAND.navyCard }}
            >
              <Waves className="w-8 h-8" style={{ color: BRAND.mediumGray }} />
            </div>
            <h3 className="text-lg font-medium mb-2" style={{ color: BRAND.white }}>
              No training sessions yet
            </h3>
            <p className="text-sm mb-6" style={{ color: BRAND.mediumGray }}>
              Start your first session to begin tracking your progress
            </p>
            <button
              type="button"
              onClick={handleStartNewSession}
              className="px-6 py-3 rounded-lg font-medium inline-flex items-center gap-2"
              style={{ backgroundColor: BRAND.red, color: BRAND.white }}
              data-testid="start-first-session-button"
            >
              <Plus className="w-5 h-5" />
              Start First Session
            </button>
          </div>
        ) : (
          <div className="space-y-3" data-testid="sessions-list">
            {sessions.map((session, index) => (
              <button
                key={session.id || index}
                onClick={() => handleViewSession(session)}
                className="w-full rounded-lg p-4 text-left transition-all hover:opacity-90"
                style={{ backgroundColor: BRAND.navyCard }}
                data-testid={`session-item-${index}`}
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    {session.mode === 'race' ? (
                      <Trophy className="w-5 h-5" style={{ color: BRAND.orange }} />
                    ) : (
                      <Waves className="w-5 h-5" style={{ color: BRAND.orange }} />
                    )}
                    <span className="text-sm font-medium capitalize" style={{ color: BRAND.white }}>
                      {session.mode} Mode
                    </span>
                    {session.heat_number > 0 && (
                      <span 
                        className="text-xs px-2 py-0.5 rounded-full"
                        style={{ backgroundColor: BRAND.navyLight, color: BRAND.white }}
                        data-testid={`heat-badge-${index}`}
                      >
                        {getHeatLabel(session.heat_number)}
                      </span>
                    )}
                    {session.auto_stopped && (
                      <span 
                        className="text-xs px-2 py-0.5 rounded-full flex items-center gap-1"
                        style={{ backgroundColor: BRAND.navyLight, color: BRAND.orange }}
                      >
                        <Zap className="w-3 h-3" />
                        Auto-stopped
                      </span>
                    )}
                  </div>
                  <ChevronRight className="w-5 h-5" style={{ color: BRAND.mediumGray }} />
                </div>
                
                <div className="grid grid-cols-3 gap-3 mb-2">
                  <div>
                    <p className="text-xs" style={{ color: BRAND.mediumGray }}>Distance</p>
                    <p className="text-base font-semibold" style={{ color: BRAND.red }}>
                      {formatDistance(session.distance_covered)}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs" style={{ color: BRAND.mediumGray }}>Time</p>
                    <p className="text-base font-semibold" style={{ color: BRAND.white }}>
                      {formatDuration(session.duration_seconds)}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs" style={{ color: BRAND.mediumGray }}>Avg Speed</p>
                    <p className="text-base font-semibold" style={{ color: BRAND.white }}>
                      {formatSpeed(session.average_speed_ms)} km/h
                    </p>
                  </div>
                </div>
                
                <p className="text-xs" style={{ color: BRAND.darkGray }}>
                  {formatDate(session.created_at)}
                </p>
              </button>
            ))}
          </div>
        )}
      </main>

      {/* Start New Session Button (Fixed at bottom) */}
      {sessions.length > 0 && (
        <div className="px-4 py-4" style={{ backgroundColor: BRAND.navyDark }}>
          <button
            type="button"
            onClick={handleStartNewSession}
            className="w-full py-4 rounded-xl font-bold flex items-center justify-center gap-2 transition-all active:scale-98"
            style={{ backgroundColor: BRAND.red, color: BRAND.white }}
            data-testid="new-session-button"
          >
            <Plus className="w-5 h-5" />
            Start New Session
          </button>
        </div>
      )}
    </div>
  );
};

export default HistoryScreen_DBPRO;
